import type {
  CountSetting,
  DensityPreset,
  HistoryRecord,
  LevelTrack,
  PuzzleData,
  PuzzleDifficulty,
  TrainSeed,
} from './types';
import { LEVEL_DATA } from './puzzles';
import { resolvePuzzle } from './puzzles/multiPuzzleUtils';
import { useGameStore } from './store';

function writeRecord(record: HistoryRecord): void {
  useGameStore.getState().recordHistory(record);
}

/** Casual progression level (single-puzzle levels rendered via `mode: 'levels'`). */
export function recordCasualLevelWin(levelIndex: number): void {
  const level = LEVEL_DATA.progression.casual[levelIndex];
  if (!level) return;
  const completion = useGameStore.getState().completions[level.id];
  writeRecord({
    id: `progression:${level.id}`,
    storageKey: level.id,
    label: `Level ${levelIndex + 1}`,
    category: 'progression',
    solution: level.solution,
    timestamp: Date.now(),
    seconds: completion?.time ?? 0,
    hints: completion?.hints ?? 0,
    screen: {
      type: 'level',
      mode: 'levels',
      puzzleIndex: levelIndex,
      entry: 'view',
    },
  });
}

/** One sub-puzzle of a multi-sub level (progression or daily track). */
export function recordMultiSubPartWin(params: {
  track: LevelTrack;
  difficulty: PuzzleDifficulty;
  levelIndex: number;
  subIndex: number;
}): void {
  const { track, difficulty, levelIndex, subIndex } = params;
  const level = LEVEL_DATA[track][difficulty]?.[levelIndex];
  if (!level) return;
  const subPuzzle = level.puzzles[subIndex];
  if (!subPuzzle) return;

  const resolved = resolvePuzzle(level, subIndex);
  const storageKey = `${level.id}:${subPuzzle.id}`;
  const completion = useGameStore.getState().completions[storageKey];

  const isDaily = track === 'daily';
  const partTotal = level.puzzles.length;
  const base = isDaily ? `Daily · ${level.name}` : `Level ${levelIndex + 1}`;
  const label =
    partTotal > 1 ? `${base} · Part ${subIndex + 1}/${partTotal}` : base;

  writeRecord({
    id: `${isDaily ? 'daily' : 'progression'}:${level.id}:${subPuzzle.id}`,
    storageKey,
    label,
    category: isDaily ? 'daily' : 'progression',
    solution: resolved.solution,
    timestamp: Date.now(),
    seconds: completion?.time ?? 0,
    hints: completion?.hints ?? 0,
    screen: {
      type: 'level',
      mode: 'multi-sub',
      track,
      difficulty,
      levelIndex,
      subIndex,
      entry: 'view',
    },
  });
}

/** Shared puzzle opened from a share URL. */
export function recordSharedWin(params: {
  search: string;
  puzzle: PuzzleData;
  label?: string;
}): void {
  const { search, puzzle, label = 'Shared Puzzle' } = params;
  const storageKey = `shared_${search}`;
  const completion = useGameStore.getState().completions[storageKey];
  writeRecord({
    id: `shared:${search}`,
    storageKey,
    label,
    category: 'shared',
    solution: puzzle.solution,
    timestamp: Date.now(),
    seconds: completion?.time ?? 0,
    hints: completion?.hints ?? 0,
    screen: { type: 'play', search },
  });
}

export interface TrainScreenSettings {
  strict: boolean;
  hintCount: CountSetting;
  validateCount: CountSetting;
  autoMark: boolean;
}

/** Practice/train puzzle. */
export function recordTrainWin(params: {
  size: number;
  seed: TrainSeed;
  density: DensityPreset;
  solution: boolean[][];
  storageKey: string;
  settings: TrainScreenSettings;
}): void {
  const { size, seed, density, solution, storageKey, settings } = params;
  const completion = useGameStore.getState().completions[storageKey];
  writeRecord({
    id: `train:${storageKey}`,
    storageKey,
    label: `Practice · ${size}×${size}`,
    category: 'train',
    solution,
    timestamp: Date.now(),
    seconds: completion?.time ?? 0,
    hints: completion?.hints ?? 0,
    screen: {
      type: 'level',
      mode: 'train',
      size,
      seed,
      density,
      strict: settings.strict,
      hintCount: settings.hintCount,
      validateCount: settings.validateCount,
      autoMark: settings.autoMark,
      entry: 'view',
    },
  });
}

